
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Globe, ShoppingCart, Target, Megaphone, Video, Users, Settings, MapPin, Mail, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const Services = () => {
  const services = [
    {
      icon: <Globe className="w-8 h-8 text-brand mb-4" />,
      title: "Δημιουργία Ιστοσελίδων",
      subtitle: "(σε 24–48 ώρες)",
      description: "Ιδανικό για επαγγελματίες και μικρές επιχειρήσεις που θέλουν άμεση online παρουσία. Ο Mike αναλαμβάνει όλη τη διαδικασία: από το design και το copywriting έως την τελική ενεργοποίηση του site.",
      features: [
        "Σχεδίαση landing page ή πλήρους site",
        "Υποστήριξη για hosting & domain",
        "CTA φόρμα",
        "Βασική βελτιστοποίηση SEO",
        "Ενσωμάτωση Google Maps, email κ.ά."
      ]
    },
    {
      icon: <ShoppingCart className="w-8 h-8 text-brand mb-4" />,
      title: "Δημιουργία eShop",
      subtitle: "(Shopify)",
      description: "Για επιχειρήσεις που θέλουν να πουλήσουν προϊόντα online, η λύση βασίζεται στο Shopify.",
      features: [
        "Στήσιμο και σχεδίαση σε Shopify",
        "Προσθήκη προϊόντων (CSV ή χειροκίνητα)",
        "Δημιουργία banners & visual assets",
        "Email automations για εγκαταλελειμμένα καλάθια",
        "SEO και διγλωσσία (εφόσον χρειάζεται)"
      ]
    },
    {
      icon: <Target className="w-8 h-8 text-brand mb-4" />,
      title: "Funnels & Landing Pages",
      subtitle: "",
      description: "Ο Mike δημιουργεί high-converting funnels για coaches, course creators και υπηρεσίες.",
      features: [
        "Στρατηγική & mapping funnel",
        "VSL landing page",
        "Upsell / Thank You page",
        "Σύνδεση με email list / αυτοματισμούς"
      ]
    },
    {
      icon: <Megaphone className="w-8 h-8 text-brand mb-4" />,
      title: "Διαφημίσεις Facebook, Instagram & TikTok",
      subtitle: "",
      description: "Στήσιμο και διαχείριση διαφημίσεων με έμφαση στο conversion.",
      features: [
        "Καμπάνιες Awareness – Leads – Sales",
        "Δημιουργία visuals / videos",
        "Scripts για video ads",
        "A/B Testing & optimization"
      ]
    },
    {
      icon: <Video className="w-8 h-8 text-brand mb-4" />,
      title: "Παραγωγή Περιεχομένου για Social Media",
      subtitle: "",
      description: "Βίντεο που τραβούν προσοχή & φέρνουν πωλήσεις. Γυρίσματα, scripts, captions, thumbnails – όλα έτοιμα.",
      features: [
        "Short-form videos (TikTok, Reels, Shorts)",
        "Video scripting & storytelling",
        "Γυρίσματα (στη Θεσσαλονίκη ή remote)",
        "Παραγωγή με hooks, captions & branding"
      ]
    },
    {
      icon: <Users className="w-8 h-8 text-brand mb-4" />,
      title: "Business Consulting & Mentoring",
      subtitle: "",
      description: "Ο Mike προσφέρει καθοδήγηση σε νέους και έμπειρους επαγγελματίες, με έμφαση σε digital στρατηγική, monetization και scaling.",
      features: [
        "Personal brands",
        "Course creators",
        "Freelancers",
        "Μικρές επιχειρήσεις που θέλουν ανάπτυξη"
      ]
    }
  ];
  
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-black">
        {/* Hero Section */}
        <section className="pt-32 pb-20">
          <div className="container mx-auto px-6 md:px-8 lg:px-12">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
                Υπηρεσίες που προσφέρει ο Mike Foto
              </h1>
              
              <div className="space-y-6 text-xl text-gray-300 leading-relaxed">
                <p>
                  Ο Mike Foto βοηθά επιχειρηματίες και επαγγελματίες να χτίσουν και να αναπτύξουν την online παρουσία τους, με έμφαση στην ταχύτητα, τη λειτουργικότητα και την καθαρή στρατηγική.
                </p>
                
                <p>
                  Όλες οι υπηρεσίες είναι προσαρμοσμένες ώστε να παραδίδονται άμεσα, χωρίς περιττές διαδικασίες και με ξεκάθαρο αποτέλεσμα.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gradient-to-b from-black to-dark-200">
          <div className="container mx-auto px-6 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {services.map((service, index) => (
                <Card key={index} className="bg-dark-300 border-gray-800 hover:border-gray-700 transition-all duration-300">
                  <CardContent className="p-8 text-left">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="flex-shrink-0">
                        {service.icon}
                      </div>
                      <div className="text-left">
                        <h3 className="text-2xl font-bold text-white mb-2 text-left">
                          {service.title}
                        </h3>
                        {service.subtitle && (
                          <p className="text-brand text-lg font-semibold mb-4 text-left">
                            {service.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-300 mb-6 leading-relaxed text-left">
                      {service.description}
                    </p>
                    
                    <div className="space-y-3 text-left">
                      <h4 className="text-white font-semibold mb-3 text-left">Περιλαμβάνει:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-3 text-left">
                            <div className="w-1.5 h-1.5 bg-brand rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-400 text-left">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand to-brand-hover">
          <div className="container mx-auto px-6 md:px-8 lg:px-12">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Έχεις επιχείρηση ή ιδέα;
              </h2>
              
              <p className="text-xl text-white/90 mb-8">
                Συμπλήρωσε τη φόρμα και ο Mike θα επικοινωνήσει μαζί σου.
              </p>
              
              <Button asChild className="bg-white hover:bg-gray-100 text-brand font-bold text-xl px-12 py-6 rounded-lg shadow-2xl hover:shadow-3xl transition-all" size="lg">
                <Link to="/contact">Συμπλήρωσε τη φόρμα</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Services;
